import React, { createContext } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";

import { Box, Drawer as MuiDrawer, IconButton, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

import Footer from "./SidebarFooter";
import SidebarNav from "./SidebarNav";
import logo from "../../vendor/logo.svg";

// LWI-720 — context exposing the desktop sidebar's collapsed state to
// descendants (consumed by SidebarNavListItem to swap to icon-only mode).
// Defaults to false so the temporary (mobile) drawer always renders the
// full menu.
export const SidebarCollapsedContext = createContext(false);

const Drawer = styled(MuiDrawer)`
  border-right: 0;

  > div {
    border-right: 0;
    scrollbar-width: none;
  }
`;

// LWI-720 — keep the original Brand markup (NavLink + logo) intact so the
// home-link and logo continue to behave exactly as before. The toggle
// button lives in its own row below the brand to avoid layout interplay.
// The logo is always rendered (no conditional), but scales down when
// collapsed so the brand area still shows something distinctive.
//
// LWI-766 — Chrome can flex-shrink Brand toward 0 in some viewport sizes
// (reproduced on 1920×1200 @ 100% zoom), and `overflow: hidden` then
// clips the whole logo. Pin the height with `min-height` + `flex-shrink:
// 0` so Brand stays at 64px regardless of sibling content.
const Brand = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  min-height: 64px;
  flex-shrink: 0;
  padding: 0 ${(props) => props.theme.spacing(props.$collapsed ? 1 : 3)};
  /* LWI-1096 — themed; every variant defines a dark header background
     so the white logo stays visible. */
  background-color: ${(props) => props.theme.sidebar.header.background};
  cursor: pointer;
  overflow: hidden;
`;

const BrandIcon = styled.img`
  width: ${(props) => (props.$collapsed ? "40px" : "200px")};
  height: auto;
  display: block;
  flex-shrink: 0;
  transition: width 0.2s;
`;

const ToggleRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-end")};
  padding: ${(props) => props.theme.spacing(0.5, props.$collapsed ? 0 : 1)};
  background-color: ${(props) => props.theme.sidebar.header.background};
  border-top: 1px solid ${(props) => alpha(props.theme.palette.common.white, 0.06)};
`;

const ToggleButton = styled(IconButton)`
  color: ${(props) => props.theme.sidebar.header.color};
  opacity: 0.7;
  padding: ${(props) => props.theme.spacing(0.5)};
  &:hover {
    opacity: 1;
    background-color: ${(props) =>
      alpha(props.theme.palette.common.white, props.theme.palette.action.selectedOpacity)};
  }
`;

const Sidebar = ({
  items,
  showFooter = true,
  collapsed = false,
  onToggleCollapse,
  ...rest
}) => {
  return (
    <Drawer variant="permanent" {...rest}>
      <Brand component={NavLink} to="/" $collapsed={collapsed}>
        <BrandIcon src={logo} alt="LOGIKweb-IQ" $collapsed={collapsed} />
      </Brand>
      {/* LWI-720 — desktop-only toggle row. The mobile (temporary) drawer
          doesn't pass onToggleCollapse, so this row hides there. */}
      {onToggleCollapse && (
        <ToggleRow $collapsed={collapsed}>
          <Tooltip
            title={collapsed ? "Expand menu" : "Collapse menu"}
            placement="right"
          >
            <ToggleButton
              size="small"
              onClick={onToggleCollapse}
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              data-testid="sidebar-toggle-collapse"
            >
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </ToggleButton>
          </Tooltip>
        </ToggleRow>
      )}
      <SidebarCollapsedContext.Provider value={collapsed}>
        <SidebarNav items={items} />
      </SidebarCollapsedContext.Provider>
      {!!showFooter && !collapsed && <Footer />}
    </Drawer>
  );
};

export default Sidebar;
